import { parseKstInstant, toKstDateStr } from "./kst-date";
import practicalExamSchedule from "./jeongcheogi-practical-exams.json";
import {
  COMPLETE_PASS_26_2_PRODUCT_ID,
  COMPLETE_PASS_26_3_PRODUCT_ID,
  COMPLETE_PASS_PRODUCT_ID,
  PASSES,
  passTypeForProductId,
  PREMIUM_PASS_26_2_PRODUCT_ID,
  PREMIUM_PASS_26_3_PRODUCT_ID,
  PREMIUM_PASS_PRODUCT_ID,
  STARTER_PASS_PRODUCT_ID,
  type PassType,
} from "../types/pass";

/** 정보처리기사 실기 시험일 공용 타입. */
export type PracticalExam = {
  sessionId: string;
  examDateISO: string;
};

/**
 * 정보처리기사 실기 시험일 SSOT의 코드용 공개 API.
 *
 * 실제 값은 한국산업인력공단 공고 제2025-208호를 옮긴
 * jeongcheogi-practical-exams.json 한 곳에서 관리한다. 판매 회차와 학습 일정 빌더가
 * 같은 JSON을 사용하므로 새 시행계획 반영 시 시험일을 여러 파일에 중복 입력하지 않는다.
 */
export const JEONGCHEOGI_PRACTICAL_EXAMS: readonly PracticalExam[] =
  practicalExamSchedule.exams;

// 판매 종료 = 실기 시험일 D-N 00:00 KST. getPremiumCompleteSalesEndAtISO 가 여기서 파생한다.
// N=1 이면 D-1일(시험 전날) 00:00 부터 중지 → 마지막 구매일은 D-2(시험 이틀 전).
// 판매 종료 안내(Membership "…부터 판매중지", 메뉴 "곧 마감")는 salesEndAtISO 에서 파생하므로
// 이 값만 바꾸면 된다. 안내 문구에 날짜를 손으로 적지 말 것 — D-3→D-1 전환 때 "마지막 구매일"
// (= 판매 종료일 − 1일)을 사람이 계산해 적다 틀린 적이 있다(JCG-3064).
const PREMIUM_COMPLETE_SALES_CUTOFF_DAYS = 1;

export const PREMIUM_COMPLETE_PASS_SALES_CLOSED_ERROR =
  "Premium·Complete 이용권 판매가 종료되었습니다.";

export type PassSalesTimeInput = number | string | Date;

export type PremiumCompletePassSalesSeason = {
  sessionId: PracticalExam["sessionId"];
  displayName: string;
  examDateISO: string;
  /** null은 이 SSOT 도입 전에 이미 판매를 시작한 현재 회차에만 사용한다. */
  salesStartAtISO: string | null;
  /** 시험일 D-1 00:00 KST. 이 시각부터 해당 회차 상품은 판매 종료다. */
  salesEndAtISO: string;
  expiresAtISO: string;
  premiumProductId: string;
  completeProductId: string;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** 실기 시험일에서 판매 종료 일자(D-1 00:00 KST)를 파생한다. */
export function getPremiumCompleteSalesEndAtISO(examDateISO: string): string {
  const examDateMs = Date.parse(`${examDateISO}T00:00:00Z`);
  const normalizedExamDate = Number.isFinite(examDateMs)
    ? new Date(examDateMs).toISOString().slice(0, 10)
    : null;

  if (normalizedExamDate !== examDateISO) {
    throw new Error(`잘못된 실기 시험일입니다: ${examDateISO}`);
  }

  const cutoffDateISO = new Date(
    examDateMs - PREMIUM_COMPLETE_SALES_CUTOFF_DAYS * DAY_MS
  )
    .toISOString()
    .slice(0, 10);
  return `${cutoffDateISO}T00:00:00+09:00`;
}

type PremiumCompletePassSalesSeasonConfig = Omit<
  PremiumCompletePassSalesSeason,
  "examDateISO" | "salesEndAtISO"
>;

function createPremiumCompletePassSalesSeason(
  config: PremiumCompletePassSalesSeasonConfig
): PremiumCompletePassSalesSeason {
  const exam = JEONGCHEOGI_PRACTICAL_EXAMS.find(
    ({ sessionId }) => sessionId === config.sessionId
  );
  if (!exam) {
    throw new Error(`실기 시험일이 없는 회차입니다: ${config.sessionId}`);
  }

  return {
    ...config,
    examDateISO: exam.examDateISO,
    salesEndAtISO: getPremiumCompleteSalesEndAtISO(exam.examDateISO),
  };
}

/**
 * Premium·Complete 회차별 판매 일정 SSOT.
 *
 * 26-3은 2026-07-19 12:00 KST에 자동 재개하며, 동일 시각에
 * exam_sessions의 2026-3 예약 활성화 기준으로도 사용된다.
 */
export const PREMIUM_COMPLETE_PASS_SALES_SEASONS = [
  createPremiumCompletePassSalesSeason({
    sessionId: "2026-2",
    displayName: "2026년 2회",
    salesStartAtISO: null,
    expiresAtISO: "2026-07-31T14:59:59.000Z",
    premiumProductId: PREMIUM_PASS_26_2_PRODUCT_ID,
    completeProductId: COMPLETE_PASS_26_2_PRODUCT_ID,
  }),
  createPremiumCompletePassSalesSeason({
    sessionId: "2026-3",
    displayName: "2026년 3회",
    salesStartAtISO: "2026-07-19T12:00:00+09:00",
    expiresAtISO: "2026-10-31T14:59:59.000Z",
    premiumProductId: PREMIUM_PASS_26_3_PRODUCT_ID,
    completeProductId: COMPLETE_PASS_26_3_PRODUCT_ID,
  }),
] as const satisfies readonly PremiumCompletePassSalesSeason[];

export type PremiumCompletePassSalesStatus = {
  isClosed: boolean;
  reason:
    | "on-sale"
    | "between-seasons"
    | "cutoff-reached"
    | "no-configured-season";
  /** 현재 판매 중이거나, 판매 공백기에 가장 최근에 시작한 회차. */
  season: PremiumCompletePassSalesSeason | null;
  /** 판매 공백기에 다음으로 열릴 회차. */
  nextSeason: PremiumCompletePassSalesSeason | null;
  exam: PracticalExam | null;
  cutoffDateISO: string | null;
  nextSalesStartAtISO: string | null;
};

function toTimeMs(input: PassSalesTimeInput = Date.now()): number {
  if (typeof input === "number") return input;
  if (input instanceof Date) return input.getTime();

  const timeMs = parseKstInstant(input).getTime();
  if (!Number.isFinite(timeMs)) {
    throw new Error(`잘못된 판매 시각입니다: ${input}`);
  }
  return timeMs;
}

function startsAtMs(season: PremiumCompletePassSalesSeason): number {
  return season.salesStartAtISO === null
    ? Number.NEGATIVE_INFINITY
    : new Date(season.salesStartAtISO).getTime();
}

function endsAtMs(season: PremiumCompletePassSalesSeason): number {
  return new Date(season.salesEndAtISO).getTime();
}

function practicalExamForSession(
  sessionId: string
): PracticalExam | null {
  return (
    JEONGCHEOGI_PRACTICAL_EXAMS.find(
      (exam) => exam.sessionId === sessionId
    ) ?? null
  );
}

/** 현재 시각에 실제 판매 중인 Premium·Complete 회차. */
export function getActivePassSalesSeason(
  now: PassSalesTimeInput = Date.now()
): PremiumCompletePassSalesSeason | null {
  const nowMs = toTimeMs(now);
  return (
    PREMIUM_COMPLETE_PASS_SALES_SEASONS.find(
      (season) => startsAtMs(season) <= nowMs && nowMs < endsAtMs(season)
    ) ?? null
  );
}

/**
 * 현재 시즌 회차. 판매 공백기에는 마지막으로 시작한 회차를 유지하고,
 * 다음 회차의 salesStartAtISO 경계에서 교체한다.
 */
export function getCurrentPassSeason(
  now: PassSalesTimeInput = Date.now()
): PremiumCompletePassSalesSeason | null {
  const nowMs = toTimeMs(now);
  let current: PremiumCompletePassSalesSeason | null = null;
  for (const season of PREMIUM_COMPLETE_PASS_SALES_SEASONS) {
    if (startsAtMs(season) <= nowMs) current = season;
  }
  return current;
}

export function getNextPassSalesSeason(
  now: PassSalesTimeInput = Date.now()
): PremiumCompletePassSalesSeason | null {
  const nowMs = toTimeMs(now);
  return (
    PREMIUM_COMPLETE_PASS_SALES_SEASONS.find(
      (season) => season.salesStartAtISO !== null && startsAtMs(season) > nowMs
    ) ?? null
  );
}

export function getPassSalesSeasonForProduct(
  productId: string
): PremiumCompletePassSalesSeason | null {
  return (
    PREMIUM_COMPLETE_PASS_SALES_SEASONS.find(
      (season) =>
        season.premiumProductId === productId ||
        season.completeProductId === productId
    ) ?? null
  );
}

/** Premium·Complete 판매 상태. 시각 경계는 KST가 포함된 ISO 시각으로 비교한다. */
export function getPremiumCompletePassSalesStatus(
  now: PassSalesTimeInput = Date.now()
): PremiumCompletePassSalesStatus {
  const activeSeason = getActivePassSalesSeason(now);
  const currentSeason = activeSeason ?? getCurrentPassSeason(now);
  const nextSeason = getNextPassSalesSeason(now);
  const exam = currentSeason
    ? practicalExamForSession(currentSeason.sessionId)
    : null;

  if (activeSeason) {
    return {
      isClosed: false,
      reason: "on-sale",
      season: activeSeason,
      nextSeason,
      exam,
      cutoffDateISO: toKstDateStr(activeSeason.salesEndAtISO),
      nextSalesStartAtISO: nextSeason?.salesStartAtISO ?? null,
    };
  }

  if (!currentSeason) {
    return {
      isClosed: true,
      reason: "no-configured-season",
      season: null,
      nextSeason,
      exam: null,
      cutoffDateISO: null,
      nextSalesStartAtISO: nextSeason?.salesStartAtISO ?? null,
    };
  }

  return {
    isClosed: true,
    reason: nextSeason ? "between-seasons" : "cutoff-reached",
    season: currentSeason,
    nextSeason,
    exam,
    cutoffDateISO: toKstDateStr(currentSeason.salesEndAtISO),
    nextSalesStartAtISO: nextSeason?.salesStartAtISO ?? null,
  };
}

export function isPremiumCompletePassType(passType: PassType): boolean {
  return passType === "premium" || passType === "complete";
}

/** PASS_PRODUCT_POLICIES에 등록된 모든 시즌의 Premium·Complete 상품을 자동 포함한다. */
export function isPremiumCompletePassProduct(productId: string): boolean {
  const passType = passTypeForProductId(productId);
  return passType ? isPremiumCompletePassType(passType) : false;
}

/** 해당 productId가 현재 판매 중인 회차와 일치할 때만 판매 가능하다. */
export function isPremiumCompletePassSaleClosed(
  productId: string,
  now: PassSalesTimeInput = Date.now()
): boolean {
  if (!isPremiumCompletePassProduct(productId)) return false;

  const activeSeason = getActivePassSalesSeason(now);
  const productSeason = getPassSalesSeasonForProduct(productId);
  return !activeSeason || productSeason?.sessionId !== activeSeason.sessionId;
}

/**
 * 결제된 이용권 상품이 현재도 유효한 권한을 부여하는지 판정한다.
 *
 * 판매 회차가 바뀌어도 이전 회차 이용권은 상품별 expiresAtISO까지 유효하다.
 * 반대로 다음 회차 상품은 salesStartAtISO 전에는 권한 복구 대상으로 보지 않는다.
 */
export function isPassProductEntitlementActive(
  productId: string,
  now: PassSalesTimeInput = Date.now()
): boolean {
  const passType = passTypeForProductId(productId);
  if (!passType) return false;
  if (passType === "starter") return true;

  const season = getPassSalesSeasonForProduct(productId);
  if (!season) return false;

  const nowMs = toTimeMs(now);
  const expiresAtMs = new Date(season.expiresAtISO).getTime();
  return startsAtMs(season) <= nowMs && nowMs < expiresAtMs;
}

/**
 * exam_sessions 예약 활성화 대상.
 * salesStartAtISO가 있는 회차 중 현재 시각까지 시작한 가장 최신 회차를 반환한다.
 */
export function getDueExamSessionActivation(
  now: PassSalesTimeInput = Date.now()
): PremiumCompletePassSalesSeason | null {
  const nowMs = toTimeMs(now);
  let due: PremiumCompletePassSalesSeason | null = null;
  for (const season of PREMIUM_COMPLETE_PASS_SALES_SEASONS) {
    if (season.salesStartAtISO && startsAtMs(season) <= nowMs) due = season;
  }
  return due;
}

// ============================================================
// 관리자 도구용 이용권 상품 목록 (JCG-3064)
// ============================================================
// 무료 이용권 지급(free-pass)·대량메일(email-sender) 화면과 지급 라우트가 회차 상품을 손으로
// 나열하지 않도록 판매 일정 SSOT에서 파생한다. 새 회차는 PREMIUM_COMPLETE_PASS_SALES_SEASONS 에
// 추가하면 세 곳에 자동으로 나타난다.

/**
 * 회차 접미사가 없는 레거시 상품(premium-pass·complete-pass)의 회차.
 * 판매 일정 SSOT 도입 전 상품이라 PREMIUM_COMPLETE_PASS_SALES_SEASONS 에 없다.
 */
const LEGACY_PASS_PRODUCT_SEASON_DISPLAY_NAME = "2026년 1회";
/** 회차 없는 레거시 Premium·Complete 상품(Complete → Premium 순). */
export const LEGACY_SEASONLESS_PASS_PRODUCT_IDS: readonly string[] = [
  COMPLETE_PASS_PRODUCT_ID,
  PREMIUM_PASS_PRODUCT_ID,
];

function seasonsNewestFirst(): PremiumCompletePassSalesSeason[] {
  return [...PREMIUM_COMPLETE_PASS_SALES_SEASONS].reverse();
}

/**
 * 관리자 화면용 이용권 상품 이름. 예: "Premium 이용권 (2026년 3회)", "Starter 이용권 (평생)".
 * 회차명은 판매 일정 SSOT(season.displayName)에서 파생한다. 이용권이 아니면 productId 를 돌려준다.
 */
export function getPassProductLabel(productId: string): string {
  const passType = passTypeForProductId(productId);
  if (!passType) return productId;

  const name = `${PASSES[passType].name} 이용권`;
  if (passType === "starter") return `${name} (평생)`;

  const seasonName =
    getPassSalesSeasonForProduct(productId)?.displayName ??
    (LEGACY_SEASONLESS_PASS_PRODUCT_IDS.includes(productId)
      ? LEGACY_PASS_PRODUCT_SEASON_DISPLAY_NAME
      : null);
  return seasonName ? `${name} (${seasonName})` : name;
}

/**
 * 관리자가 무료로 지급할 수 있는 이용권 상품 — Starter + 판매 회차별 Premium·Complete(최신 회차 먼저).
 *
 * 레거시 premium-pass·complete-pass 는 넣지 않는다. 회차가 없어 지급 시 현재 회차 만료일을 받는데
 * productId 는 2026년 1회 상품이라 주문 기록과 실제 권한이 어긋나고, 회차 기준 판정
 * (isPassProductEntitlementActive)은 그 주문을 유효 권한으로 보지 않는다.
 */
export const GRANTABLE_PASS_PRODUCT_IDS: readonly string[] = [
  STARTER_PASS_PRODUCT_ID,
  ...seasonsNewestFirst().flatMap((season) => [
    season.premiumProductId,
    season.completeProductId,
  ]),
];

/**
 * 구매자 필터용 전체 이용권 상품 — 판매 회차(최신 먼저, Complete → Premium) → 레거시 → Starter.
 * PASS_PRODUCT_POLICIES 의 키를 빠짐없이 포함한다(passSales.test 가 강제).
 */
export const PASS_PRODUCT_IDS_NEWEST_FIRST: readonly string[] = [
  ...seasonsNewestFirst().flatMap((season) => [
    season.completeProductId,
    season.premiumProductId,
  ]),
  ...LEGACY_SEASONLESS_PASS_PRODUCT_IDS,
  STARTER_PASS_PRODUCT_ID,
];
